// PurchaseEnergyBundleContent.tsx

import energyCanCase from "../../../assets/static/images/drawers/purchase-energy-bundle/energy-can-case.webp";
import energyCan from "../../../assets/static/images/symbols/v1/symbol-energy-drink.webp";
import { ButtonVar1 } from "../../common/ButtonVar1/ButtonVar1";
import { Trans, useTranslation } from "react-i18next";
import "./PurchaseEnergyBundleContent.scss";
import OutlineStrokedShareIcon from "../../../assets/static/icons/outline-stroked-share-icon-2.svg?react";
import shineRay from "../../../assets/static/icons/shine-var2.svg";
import { getActiveLocale } from "../../../logic/i18n/localePreference";
import type { SupportedLocale } from "../../../logic/i18n/locales";
import { haptics } from "../../../logic/helpers/haptics";

interface PurchaseEnergyBundleContentProps {
    hasEnergy: boolean;
    onPurchase?: () => void;
    onShare: () => void;
    hasSuccessfulShare: boolean;
    qualifiedCount: number;
    requiredCount: number;
    shareDisabled?: boolean;
    shareLabel?: string;
    message?: string | null;
}

const OR_TEXT_ASSET_PREFIX = "../../../assets/static/icons/or-texts/or-text__";
const orTextAssets = import.meta.glob<string>(
    "../../../assets/static/icons/or-texts/or-text__*.svg",
    { eager: true, import: "default" },
);
const defaultOrTextAsset =
    orTextAssets[`${OR_TEXT_ASSET_PREFIX}en.svg`] ??
    (() => {
        throw new Error("Missing default localized OR text asset");
    })();

function getOrTextAsset(locale: SupportedLocale): string {
    return (
        orTextAssets[`${OR_TEXT_ASSET_PREFIX}${locale.toLowerCase()}.svg`] ??
        defaultOrTextAsset
    );
}

function EnergyTitle({ hasEnergy }: { hasEnergy: boolean }) {
    const { t } = useTranslation();
    const firstLine = t(
        hasEnergy
            ? "purchaseEnergy.title.available.firstLine"
            : "purchaseEnergy.title.firstLine",
    );
    const secondLine = t(
        hasEnergy
            ? "purchaseEnergy.title.available.secondLine"
            : "purchaseEnergy.title.secondLine",
    );
    const ariaLabel = hasEnergy
        ? t("mainPage.energy.ariaLabel")
        : t("purchaseEnergy.title.ariaLabel");

    return (
        <div className="purchase-energy-bundle-content__title">
            <h2
                className="purchase-energy-bundle-content__title-text"
                data-title={`${firstLine}\n${secondLine}`}
                aria-label={ariaLabel}
            >
                <span
                    className="purchase-energy-bundle-content__title-shadow"
                    aria-hidden="true"
                >
                    <span>{firstLine}</span>
                    <span>{secondLine}</span>
                </span>
                <span
                    className="purchase-energy-bundle-content__title-fill"
                    aria-hidden="true"
                >
                    <span>{firstLine}</span>
                    <span>{secondLine}</span>
                </span>
            </h2>
        </div>
    );
}

export function PurchaseEnergyBundleContent({
    hasEnergy,
    onPurchase,
    onShare,
    hasSuccessfulShare,
    qualifiedCount,
    requiredCount,
    shareDisabled = false,
    shareLabel,
    message,
}: PurchaseEnergyBundleContentProps) {
    const { t } = useTranslation();
    const orTextAsset = getOrTextAsset(getActiveLocale());

    return (
        <section
            className="purchase-energy-bundle-content"
            tabIndex={-1}
            autoFocus
        >
            <EnergyTitle hasEnergy={hasEnergy} />

            <p className="purchase-energy-bundle-content__subtitle">
                <Trans
                    i18nKey="purchaseEnergy.subtitle.text"
                    components={{ bundle: <strong /> }}
                />
            </p>

            <div
                className="purchase-energy-bundle-content__bundle"
                aria-label={t("purchaseEnergy.bundleAriaLabel", {
                    amount: 50,
                })}
            >
                <div className="purchase-energy-bundle-content__case-wrap">
                    <div
                        className="purchase-energy-bundle-content__shine"
                        aria-hidden="true"
                    >
                        {Array.from({ length: 6 }, (_, index) => (
                            <img
                                key={index}
                                className="purchase-energy-bundle-content__shine-ray"
                                src={shineRay}
                                alt=""
                                draggable={false}
                            />
                        ))}
                    </div>
                    <img
                        className="purchase-energy-bundle-content__case"
                        src={energyCanCase}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                    />
                </div>
                <img
                    className="purchase-energy-bundle-content__can"
                    src={energyCan}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                />
                <strong
                    className="purchase-energy-bundle-content__amount"
                    dir="ltr"
                >
                    x50
                </strong>
            </div>

            <div className="purchase-energy-bundle-content__actions">
                <ButtonVar1
                    className="purchase-energy-bundle-content__button"
                    text={t("purchaseEnergy.purchase.price")}
                    disabled={!onPurchase}
                    aria-label={t("purchaseEnergy.purchase.ariaLabel", {
                        price: "$4.99",
                    })}
                    onClick={() => {
                        haptics.impact("light");
                        onPurchase?.();
                    }}
                />

                <div className="purchase-energy-bundle-content__or">
                    <img
                        src={orTextAsset}
                        alt={t("purchaseEnergy.or")}
                        draggable={false}
                    />
                </div>

                <ButtonVar1
                    className="purchase-energy-bundle-content__button purchase-energy-bundle-content__button--share"
                    content={
                        <div className="pebcbsc">
                            <span className="pebcbsc__main">
                                <OutlineStrokedShareIcon />
                                <p>{shareLabel ?? t("purchaseEnergy.share")}</p>
                            </span>
                            <small className="pebcbsc__sub">
                                {hasSuccessfulShare
                                    ? t("purchaseEnergy.invitedProgress", {
                                          qualifiedCount,
                                          requiredCount,
                                      })
                                    : t("purchaseEnergy.shareWithFriends", {
                                          count: requiredCount,
                                      })}
                            </small>
                            <div className="pebcbsc__free">
                                <div className="pebcbsc__free__inner">
                                    <p>{t("purchaseEnergy.free")}</p>
                                </div>
                            </div>
                        </div>
                    }
                    disabled={shareDisabled}
                    onClick={() => {
                        haptics.impact("light");
                        onShare();
                    }}
                />
            </div>

            {message && (
                <p
                    className="purchase-energy-bundle-content__message"
                    role="status"
                >
                    {message}
                </p>
            )}
        </section>
    );
}
